import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import "./PublishDialog.scss";
import { API_BASE_URL } from "../../config";

function absoluteUrl(path) {
    if (!path) return '';
    if (/^https?:/i.test(path)) return path;
    return `${API_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
}

function countWordsInDoc(doc) {
    if (!doc || typeof doc !== 'object') return 0;
    let text = '';
    const walk = (node) => {
        if (!node) return;
        if (typeof node.text === 'string') text += node.text + ' ';
        (node.content || []).forEach(walk);
    };
    walk(doc);
    return text.trim().split(/\s+/).filter(Boolean).length;
}

export default function PublishDialog({
    open,
    onClose,
    onConfirm,
    onUnpublish,
    submitting,
    isAlreadyPublished,
    book,
    chapters,
    // Agreement to the publishing guidelines: asked once per author (and again
    // after the guidelines change), only when a book is about to go public.
    requireGuidelines = false,
    guidelinesAccepted = false,
    onGuidelinesChange,
}) {
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    const totalWords = useMemo(
        () => (chapters || []).reduce((sum, c) => sum + countWordsInDoc(c.content), 0),
        [chapters]
    );

    if (!open) return null;

    const needsAgreement = requireGuidelines && !isAlreadyPublished;

    const missing = [];
    if (!book.title?.trim()) missing.push('عنوان الكتاب');
    if (!chapters?.length) missing.push('فصل واحد على الأقل');
    if (chapters?.length && chapters.every(c => countWordsInDoc(c.content) === 0)) {
        missing.push('محتوى في فصل واحد على الأقل');
    }

    return (
        <div className="pd-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label="مراجعة قبل النشر">
            <div className="pd-panel" onClick={(e) => e.stopPropagation()}>
                <header className="pd-header">
                    <h2>{isAlreadyPublished ? 'مراجعة وتحديث المنشور' : 'مراجعة قبل النشر'}</h2>
                    <button className="pd-close" onClick={onClose} aria-label="إغلاق">×</button>
                </header>

                <div className="pd-body">
                    <p className="pd-intro">
                        راجع التفاصيل التالية قبل {isAlreadyPublished ? 'تحديث النسخة المنشورة' : 'النشر في المكتبة'}.
                        يمكن للقراء حينها رؤية الكتاب وقراءته.
                    </p>

                    <div className="pd-preview">
                        <div className="pd-cover">
                            {book.coverImage ? (
                                <img src={absoluteUrl(book.coverImage)} alt="غلاف" />
                            ) : (
                                <span>بدون غلاف</span>
                            )}
                        </div>

                        <div className="pd-info">
                            <h3 className="pd-title">{book.title || 'بدون عنوان'}</h3>
                            <p className="pd-author">بقلم {book.displayAuthor || book.author}</p>

                            <dl className="pd-meta">
                                <div>
                                    <dt>التصنيف</dt>
                                    <dd>{book.categoryLabel || 'غير محدد'}</dd>
                                </div>
                                <div>
                                    <dt>عدد الفصول</dt>
                                    <dd>{chapters?.length ?? 0}</dd>
                                </div>
                                <div>
                                    <dt>إجمالي الكلمات</dt>
                                    <dd>{totalWords.toLocaleString('ar-EG')}</dd>
                                </div>
                                <div>
                                    <dt>اسم المؤلف</dt>
                                    <dd>{book.isAnonymous ? 'مجهول' : (book.penName || book.author)}</dd>
                                </div>
                            </dl>

                            {book.description && (
                                <div className="pd-description">
                                    <h4>النبذة</h4>
                                    <p>{book.description}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {missing.length > 0 && (
                        <div className="pd-missing">
                            <strong>قبل النشر، الرجاء إضافة:</strong>
                            <ul>{missing.map(m => <li key={m}>{m}</li>)}</ul>
                        </div>
                    )}

                    {needsAgreement && (
                        <label className="pd-guidelines">
                            <input
                                type="checkbox"
                                checked={guidelinesAccepted}
                                onChange={(e) => onGuidelinesChange?.(e.target.checked)}
                            />
                            <span>
                                قرأت{' '}
                                <Link to="/guidelines" target="_blank" rel="noopener noreferrer">إرشادات النشر</Link>
                                {' '}وأوافق عليها، وأقرّ بأنني المسؤول الوحيد عن محتوى ما أنشره.
                            </span>
                        </label>
                    )}
                </div>

                <footer className="pd-footer">
                    {isAlreadyPublished && (
                        <button
                            type="button"
                            className="pd-unpublish"
                            onClick={onUnpublish}
                            disabled={submitting}
                        >
                            إلغاء النشر
                        </button>
                    )}
                    <div className="pd-footer-end">
                        <button type="button" className="pd-cancel" onClick={onClose} disabled={submitting}>
                            رجوع
                        </button>
                        <button
                            type="button"
                            className="pd-confirm"
                            onClick={onConfirm}
                            disabled={submitting || missing.length > 0 || (needsAgreement && !guidelinesAccepted)}
                        >
                            {submitting
                                ? 'جارٍ النشر…'
                                : (isAlreadyPublished ? 'تحديث المنشور' : 'تأكيد النشر')}
                        </button>
                    </div>
                </footer>
            </div>
        </div>
    );
}
